import { z } from 'zod';

import { occurrenceSchema, refineMarketInput } from '@/shared/lib/datetime-local';

/** The saved period, which untouched fields send back unchanged. */
export interface GlobalDiscountWindow {
  startsAt: string;
  endsAt: string;
}

/** Mirrors the Admin API validation (AdminGlobalDiscountRequest, DB-GD-01 CHECKs); the backend re-checks every rule. */
const globalDiscountObject = z.object({
  percentOff: z
    .number({ error: 'Enter a percent.' })
    .min(1, 'At least 1%.')
    .max(90, 'At most 90% (the payment gateway cannot charge 0).')
    .refine((value) => Number.isInteger(value * 100), 'At most 2 decimals.'),
  maxDiscountAmount: z
    .number({ error: 'Enter a cap or leave it empty.' })
    .positive('The cap must be more than 0.')
    .nullable(),
  /** datetime-local values in the discount's market zone; converted to UTC instants at submit. */
  startsAt: z.string().min(1, 'Choose a date and time.'),
  startsAtOccurrence: occurrenceSchema,
  endsAt: z.string().min(1, 'Choose a date and time.'),
  endsAtOccurrence: occurrenceSchema,
  bannerEnabled: z.boolean(),
  bannerTitleEn: z.string().max(100, 'At most 100 characters.'),
  bannerTitleAr: z.string().max(100, 'At most 100 characters.'),
  bannerTextEn: z.string().max(300, 'At most 300 characters.'),
  bannerTextAr: z.string().max(300, 'At most 300 characters.'),
  description: z.string().max(200, 'At most 200 characters.'),
});

export type GlobalDiscountFormValues = z.infer<typeof globalDiscountObject>;

/**
 * The global discount form, with its period entered in the discount's market zone and compared as
 * UTC instants once a time that does not exist or happens twice in the zone has been resolved.
 */
export function globalDiscountSchema(zone: string, original?: GlobalDiscountWindow) {
  return globalDiscountObject.superRefine((value, ctx) => {
    const start = refineMarketInput(
      ctx,
      {
        path: 'startsAt',
        occurrencePath: 'startsAtOccurrence',
        input: value.startsAt,
        occurrence: value.startsAtOccurrence,
        original: original?.startsAt,
      },
      zone,
    );
    const end = refineMarketInput(
      ctx,
      {
        path: 'endsAt',
        occurrencePath: 'endsAtOccurrence',
        input: value.endsAt,
        occurrence: value.endsAtOccurrence,
        original: original?.endsAt,
      },
      zone,
    );
    if (start && end && end <= start)
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'The end must be after the start.',
      });
    if (value.bannerEnabled && value.bannerTitleEn.trim() === '')
      ctx.addIssue({
        code: 'custom',
        path: ['bannerTitleEn'],
        message: 'Give the banner an English title.',
      });
    if (value.bannerEnabled && value.bannerTitleAr.trim() === '')
      ctx.addIssue({
        code: 'custom',
        path: ['bannerTitleAr'],
        message: 'Give the banner an Arabic title.',
      });
  });
}
